import React, { useState } from "react";
import { InfoBanner, ModalDelete, TableHeader, WithModulesAccess, WithRoleAccess } from "@services/ui-components";
import { Dropdown, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import Spinner from "@services/ui-components/src/Spinner/Spinner";
import UpdateRecoveryServiceForm from "./UpdateRecoveryServiceForm";
import { ApolloError, useReactiveVar } from "@apollo/client";
import { ErrorModalComponent } from "@me-pos/error-modal";
import i18next from "i18next";
import ErrorService from "../../../services/ErrorService";
import { useGetOneServiceQuery } from "@me-team/host/main/services/graphql/services.hooks";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import { CompanyAccess } from "@me-team/host/main/graphql/types";
import { useService } from "@me-team/common/src/hooks/useService";
import { AllModulesTagName } from "../../../utils/enums";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";

const UpdateService: React.FC = () => {
    const { t } = useTranslation();
    const { id, branchId } = useParams();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const [isErrorModalOpen, setIsErrorModalOpen] = useState<number>(null);
    const [showCannotDelete, setShowCannotDelete] = useState(false);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const navigate = useNavigate();
    const canEditServiceAndOnlineBooking = useRoleAccess(CompanyAccess.ServiceAndOnlineBookingEdit);

    const { data: oneServiceData, loading } = useGetOneServiceQuery({
        variables: {
            id: +id,
            companyId: +currentCompanyId,
            branchId: Number(branchId),
        },
        skip: !currentCompanyId,
        context: {
            errorType: "global",
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });
    const service = oneServiceData?.user?.company?.services?.services[0];

    const { handleDelete } = useService({
        service,
        onCompleted: () =>
            navigate(`/services/service/${currentCompanyId}/service-list/${branchId}`, {
                state: { text: t("Deleted"), toast: true },
            }),
        onError: (error: ApolloError) => setIsErrorModalOpen(ErrorService.errorHandling(error)),
    });

    const [showDropdownActions, setShowDropdownActions] = useState<boolean>(false);
    const handleToggleDropdownActions = () => setShowDropdownActions(!showDropdownActions);
    const handleConfirmationClose = () => setShowDeleteConfirmation(false);
    const handleCannotDeleteClose = () => setShowCannotDelete(false);

    const handleShowModalDelete = () => {
        if (service?.employeeServiceRelations?.length < 1) {
            setShowDeleteConfirmation(true);
        } else {
            setShowCannotDelete(true);
        }
    };

    if (loading || !currentCompanyId) return <Spinner />;

    return (
        <div className="mb-3">
            <InfoBanner>
                <p className="fs-7 mb-0">
                    {t(
                        "On this page, you can create a new service. After creating" +
                            " it, be sure to select the related employees to make the service available for booking."
                    )}
                </p>
            </InfoBanner>
            <TableHeader
                title={`${t("Editing Service")} ${service?.name}`}
                paths={[
                    {
                        path: `/services/service/${currentCompanyId}/service-list/${id}/edit/${branchId}`,
                        label: t("services"),
                    },
                    {
                        path: `/services/service/${currentCompanyId}/service-list/${branchId}`,
                        label: t("Service list"),
                    },
                    {
                        path: `/services/service/${currentCompanyId}/service-list/${id}/edit/${branchId}`,
                        label: `${t("Editing Service")} ${service?.name}`,
                    },
                ]}
                buttons={
                    <div className="d-flex justify-content-end mt-4 mt-lg-0">
                        <div className="col-12 col-sm-4 col-xl-4 col-xxl-3">
                            <Dropdown
                                className="dropdown-action"
                                show={showDropdownActions}
                                onToggle={handleToggleDropdownActions}
                                drop={"down-centered"}
                            >
                                <Dropdown.Toggle variant="outline-primary" id="actionsDropdown" className="w-100">
                                    <div className="d-flex justify-content-between">
                                        <span className="me-2">{t("Actions")}</span>
                                        <i
                                            className={`bi bi-chevron-down transition-icon ${showDropdownActions ? "rotate-up" : ""}`}
                                        ></i>
                                    </div>
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                    <WithModulesAccess modules={[AllModulesTagName.PERSONNEL]}>
                                        <WithRoleAccess permission={CompanyAccess.ServiceEmployeesView}>
                                            <Dropdown.Item
                                                as={Link}
                                                to={`/services-providers/${id}/${branchId}`}
                                                className="py-2"
                                            >
                                                <i className="bi bi-people me-2"></i>
                                                {t("Related employees")}
                                            </Dropdown.Item>
                                        </WithRoleAccess>
                                    </WithModulesAccess>
                                    <WithModulesAccess modules={[AllModulesTagName.WAREHOUSES]}>
                                        <WithRoleAccess permission={CompanyAccess.ServiceTechMapsView}>
                                            <Dropdown.Item
                                                as={Link}
                                                to={`/services/service/${currentCompanyId}/service-list/${service?.id}/technological/maps/branch/${branchId}`}
                                                className="py-2"
                                            >
                                                <i className="bi bi-map me-2"></i>
                                                {t("Technological maps of the" + " service")}
                                            </Dropdown.Item>
                                        </WithRoleAccess>
                                    </WithModulesAccess>
                                    <WithRoleAccess permission={CompanyAccess.ServiceDeleteRestore}>
                                        <Dropdown.Item className="py-2" onClick={handleShowModalDelete}>
                                            <i className="bi bi-trash3 me-2"></i>
                                            {t("Remove")}
                                        </Dropdown.Item>
                                    </WithRoleAccess>
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>
                    </div>
                }
                colSize={7}
                btnColSize={5}
            />
            <Row>
                <UpdateRecoveryServiceForm canEditService={canEditServiceAndOnlineBooking} />
            </Row>
            <ModalDelete
                show={showDeleteConfirmation}
                handleClose={handleConfirmationClose}
                title={t("Deleting a service")}
                body={t("Are you sure you want to delete the selected service?")}
                buttons={[
                    {
                        text: t("Cancel"),
                        onClick: handleConfirmationClose,
                        variant: "btn btn-outline-violet border-primary text-primary fw-normal",
                        className: "d-flex justify-content-center align-items-center w-100",
                    },
                    {
                        text: t("delete"),
                        onClick: handleDelete,
                        variant: "primary",
                        className: "w-100",
                    },
                ]}
            />
            <ModalDelete
                show={showCannotDelete}
                handleClose={handleCannotDeleteClose}
                title={t("It is not possible to delete the service")}
                variantTitle="text-danger"
                body={t(
                    "Deletion is not possible. The service has related employees. Clear the executors to complete the removal of the service."
                )}
                buttons={[
                    {
                        text: t("servicesPage.deleteModal.okBtn"),
                        onClick: handleCannotDeleteClose,
                        variant: "danger",
                        className: "w-100",
                    },
                ]}
            />
            {isErrorModalOpen ? (
                <ErrorModalComponent
                    i18n={i18next}
                    onClose={() => {
                        setIsErrorModalOpen(null);
                    }}
                    isOpen={!!isErrorModalOpen}
                    currentError={isErrorModalOpen}
                />
            ) : null}
        </div>
    );
};

export default UpdateService;
